import React from 'react';
import {
  Button,
  Dialog,
  DialogFooter,
  DialogTrigger,
  Flex,
  Progress,
  Select,
  Text,
  TextArea,
  TextField,
  type SelectOption,
} from '@chatsdet/ui';
import { Cube, GithubLogo, Stack, UploadSimple } from '@phosphor-icons/react';
import { useAuth } from '../../../contexts/AuthContext';
import { usePropertySummaries } from '../../../hooks/usePropertyMetadata';
import {
  uploadExternalTestBundle,
  useCreateTestSuite,
  type CreateExternalSuiteInput,
  type SuiteFailureMode,
} from '../../../features/test-suites';
import { useExternalSuiteConfigData } from '../../../features/test-suites/useExternalSuiteConfigData';
import { RepoPicker } from './external/RepoPicker';

const FAILURE_MODE_OPTIONS: SelectOption[] = [
  { id: 'continue', label: 'Continue through all layers' },
  { id: 'stop_on_layer_failure', label: 'Stop if a layer fails' },
];

const FRAMEWORK_OPTIONS: SelectOption[] = [
  { id: 'selenium', label: 'Selenium', description: 'Java, Maven or Gradle' },
  { id: 'playwright', label: 'Playwright', description: 'Node.js and Chromium' },
];

const BUNDLE_MAX_BYTES = 100 * 1024 * 1024;

type SuiteKindChoice = 'native' | 'external';
type ExternalSourceKind = 'github' | 'upload' | 'image';

/** One of the two cards on the first step. */
function KindCard({
  isSelected,
  onSelect,
  isDisabled = false,
  icon,
  title,
  description,
}: {
  isSelected: boolean;
  onSelect: () => void;
  isDisabled?: boolean;
  icon: React.ReactNode;
  title: string;
  description: string;
}) {
  return (
    <button
      type="button"
      onClick={() => {
        if (!isDisabled) onSelect();
      }}
      disabled={isDisabled}
      aria-pressed={isSelected}
      aria-disabled={isDisabled || undefined}
      className={`flex-1 rounded-lg border p-4 text-left transition-colors ${
        isSelected
          ? 'border-primary bg-primary/5'
          : 'border-border bg-card hover:border-primary/40 hover:bg-accent/30'
      }`}
    >
      <Flex align="center" gap="sm" className="mb-2">
        <span className="text-muted-foreground">{icon}</span>
        <Text weight="semibold" size="sm">
          {title}
        </Text>
      </Flex>
      <Text size="xs" className="text-muted-foreground">
        {description}
      </Text>
    </button>
  );
}

/**
 * New-suite dialog.
 *
 * @remarks Gains a type step ONLY when the org has `externalTestSuites`. For
 * everyone else the dialog is byte-identical to what it has always been — a
 * type picker with one option is a worse dialog, not a more honest one.
 *
 * The external branch asks for the minimum that makes a suite addressable: a
 * name, a framework, an application, a repository, and (optionally) where to
 * point it. The `.env` mapping is deliberately NOT here — it needs the repo
 * picked to be meaningful, it is the part customers iterate on, and a wizard
 * step you have to get right before you can save is the wrong place for it.
 */
export function CreateSuiteDialog({
  isOpen,
  onOpenChange,
  onCreated,
}: {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (suiteId: string) => void;
}) {
  const { user } = useAuth();
  const externalEnabled = Boolean(user?.features?.externalTestSuites);

  const [kind, setKind] = React.useState<SuiteKindChoice>('native');
  const [name, setName] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [failureMode, setFailureMode] = React.useState<SuiteFailureMode>('continue');
  const [error, setError] = React.useState<string | null>(null);

  // External-only fields.
  const [propertyId, setPropertyId] = React.useState<string | null>(null);
  const [repoId, setRepoId] = React.useState<string | null>(null);
  const [repoRef, setRepoRef] = React.useState('');
  const [environmentId, setEnvironmentId] = React.useState<string | null>(null);
  const [credentialId, setCredentialId] = React.useState<string | null>(null);
  const [sourceKind, setSourceKind] = React.useState<ExternalSourceKind>('github');
  const [provider, setProvider] = React.useState<'selenium' | 'playwright'>('selenium');
  const [bundle, setBundle] = React.useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = React.useState<number | null>(null);
  const [pendingUploadSuiteId, setPendingUploadSuiteId] = React.useState<string | null>(null);
  const [uploadTransactionPending, setUploadTransactionPending] = React.useState(false);
  const bundleInputRef = React.useRef<HTMLInputElement>(null);
  const uploadInFlightRef = React.useRef(false);
  const wasOpenRef = React.useRef(isOpen);

  const create = useCreateTestSuite();
  const properties = usePropertySummaries({ enabled: isOpen && externalEnabled });
  // The same loader the config page uses, pointed at the application chosen
  // here rather than at a runner that does not exist yet — so the repos,
  // environments and personas offered at create time are exactly the ones the
  // suite will show a moment later.
  const external = useExternalSuiteConfigData(kind === 'external' ? propertyId : null);

  React.useEffect(() => {
    const justOpened = isOpen && !wasOpenRef.current;
    wasOpenRef.current = isOpen;

    // Keep the already-created suite and selected bundle available when an
    // upload fails and the user closes/reopens the dialog to retry it.
    if (!justOpened || pendingUploadSuiteId) return;
    setKind('native');
    setName('');
    setDescription('');
    setFailureMode('continue');
    setPropertyId(null);
    setRepoId(null);
    setRepoRef('');
    setEnvironmentId(null);
    setCredentialId(null);
    setSourceKind('github');
    setProvider('selenium');
    setBundle(null);
    setUploadProgress(null);
    setPendingUploadSuiteId(null);
    setUploadTransactionPending(false);
    setError(null);
  }, [isOpen, pendingUploadSuiteId]);

  // A repo belongs to one property, so switching applications invalidates every
  // downstream pick rather than leaving a cross-property selection the API would
  // reject with a link-mismatch error the customer cannot act on.
  const chooseProperty = (id: string) => {
    setPropertyId(id);
    setRepoId(null);
    setEnvironmentId(null);
    setCredentialId(null);
  };

  const canSubmit = uploadTransactionPending
    ? false
    : pendingUploadSuiteId
      ? bundle !== null && uploadProgress === null
      : name.trim().length > 0 &&
        !create.isPending &&
        uploadProgress === null &&
        (kind === 'native' ||
          (sourceKind === 'github' && propertyId !== null && repoId !== null) ||
          (sourceKind === 'upload' && propertyId !== null && bundle !== null) ||
          (sourceKind === 'image' && propertyId !== null && environmentId !== null));

  const chooseSource = (next: ExternalSourceKind) => {
    setSourceKind(next);
    setError(null);
    if (next === 'image') setEnvironmentId(null);
    if (next === 'upload' || next === 'image') {
      setProvider('playwright');
      setRepoId(null);
      setRepoRef('');
    }
  };

  const chooseBundle = (file: File | undefined) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.tar.gz')) {
      setBundle(null);
      setError('Choose a .tar.gz bundle.');
      return;
    }
    if (file.size > BUNDLE_MAX_BYTES) {
      setBundle(null);
      setError('Bundles must be 100 MB or smaller.');
      return;
    }
    setError(null);
    setBundle(
      file.type === 'application/gzip'
        ? file
        : new File([file], file.name, { type: 'application/gzip', lastModified: file.lastModified })
    );
    setUploadProgress(null);
  };

  const handleDialogOpenChange = (open: boolean) => {
    if (!open && uploadInFlightRef.current) return;
    onOpenChange(open);
  };

  const submit = async () => {
    if (!canSubmit) return;
    setError(null);
    try {
      let suiteId: string;
      if (kind === 'external') {
        if (sourceKind === 'upload' && pendingUploadSuiteId) {
          suiteId = pendingUploadSuiteId;
        } else {
          let externalInput: CreateExternalSuiteInput;
          if (sourceKind === 'github') {
            if (!repoId) return;
            externalInput = {
              sourceKind: 'github',
              provider,
              propertyGithubRepoId: repoId,
              repoRef: repoRef.trim() || null,
              targetEnvironmentId: environmentId,
              credentialId,
            };
          } else {
            if (!propertyId) return;
            if (sourceKind === 'image') {
              if (!environmentId) return;
              externalInput = {
                sourceKind: 'image',
                provider: 'playwright',
                propertyId,
                targetEnvironmentId: environmentId,
                credentialId,
              };
            } else {
              externalInput = {
                sourceKind: 'upload',
                provider: 'playwright',
                propertyId,
                targetEnvironmentId: environmentId,
                credentialId,
              };
            }
          }
          if (sourceKind === 'upload') {
            uploadInFlightRef.current = true;
            setUploadTransactionPending(true);
          }
          suiteId = (
            await create.mutateAsync({
              name: name.trim(),
              description: description.trim() || null,
              kind: 'external',
              external: externalInput,
            })
          ).id;
        }
      } else {
        suiteId = (
          await create.mutateAsync({
            name: name.trim(),
            description: description.trim() || null,
            failureMode,
          })
        ).id;
      }
      if (kind === 'external' && sourceKind === 'upload' && bundle) {
        setPendingUploadSuiteId(suiteId);
        setUploadProgress(0);
        uploadInFlightRef.current = true;
        setUploadTransactionPending(true);
        await uploadExternalTestBundle(suiteId, bundle, setUploadProgress);
        uploadInFlightRef.current = false;
      }
      uploadInFlightRef.current = false;
      setUploadTransactionPending(false);
      setUploadProgress(null);
      handleDialogOpenChange(false);
      setPendingUploadSuiteId(null);
      onCreated(suiteId);
    } catch (err) {
      uploadInFlightRef.current = false;
      setUploadTransactionPending(false);
      setUploadProgress(null);
      setError(err instanceof Error ? err.message : 'Failed to create this suite');
    }
  };

  const propertyOptions: SelectOption[] = (properties.data ?? []).map((p) => ({
    id: p.id,
    label: p.name,
    description: p.baseUrl ?? undefined,
  }));
  const environmentOptions: SelectOption[] = (external.environments.data?.items ?? [])
    .filter((env) => sourceKind !== 'image' || Boolean(env.sandboxV2Environment))
    .map((env) => ({ id: env.id, label: env.name, description: env.url }));
  const credentialOptions: SelectOption[] = (external.credentials.data?.items ?? []).map(
    (cred) => ({ id: cred.id, label: cred.name })
  );

  return (
    <DialogTrigger isOpen={isOpen} onOpenChange={handleDialogOpenChange} size="lg">
      <span />
      <Dialog
        title="New Test Suite"
        eyebrow="Suites"
        description="Group related tests so they run, pass, and report together."
      >
        <Flex direction="column" gap="md">
          {externalEnabled && (
            <Flex gap="sm">
              <KindCard
                isSelected={kind === 'native'}
                onSelect={() => setKind('native')}
                isDisabled={pendingUploadSuiteId !== null}
                icon={<Stack size={18} />}
                title="Cofactor tests"
                description="Arrange flows you built here into layers that run in order."
              />
              <KindCard
                isSelected={kind === 'external'}
                onSelect={() => setKind('external')}
                isDisabled={pendingUploadSuiteId !== null}
                icon={<GithubLogo size={18} />}
                title="Your own test suite"
                description="Run an existing Selenium or Playwright suite on our infrastructure."
              />
            </Flex>
          )}

          <TextField
            label="Name"
            placeholder={
              kind === 'external' ? 'e.g. Web e2e regression' : 'e.g. Checkout regression'
            }
            value={name}
            onChange={setName}
            isDisabled={pendingUploadSuiteId !== null}
            isRequired
            autoFocus
          />
          <TextArea
            label="Description"
            placeholder="What does this suite cover?"
            value={description}
            onChange={setDescription}
            isDisabled={pendingUploadSuiteId !== null}
          />

          {kind === 'native' ? (
            <Select
              label="On layer failure"
              description="Layers run one after another; tests within a layer run in parallel. Choose whether a failing layer halts the remaining layers."
              items={FAILURE_MODE_OPTIONS}
              selectedKey={failureMode}
              onSelectionChange={(key) => setFailureMode(String(key) as SuiteFailureMode)}
            />
          ) : (
            <>
              <Select
                label="Framework"
                description={
                  sourceKind === 'image'
                    ? 'Playwright runs from the application image in the selected Sandbox v2 environment.'
                    : 'Cofactor supplies the container image and the commands for the framework you pick.'
                }
                items={FRAMEWORK_OPTIONS}
                selectedKey={provider}
                isDisabled={sourceKind !== 'github' || pendingUploadSuiteId !== null}
                onSelectionChange={(key) => setProvider(String(key) as 'selenium' | 'playwright')}
              />
              <Flex gap="sm">
                <KindCard
                  isSelected={sourceKind === 'image'}
                  onSelect={() => chooseSource('image')}
                  isDisabled={pendingUploadSuiteId !== null}
                  icon={<Cube size={18} />}
                  title="Application image"
                  description="Run Playwright from the environment’s application image."
                />
                <KindCard
                  isSelected={sourceKind === 'github'}
                  onSelect={() => chooseSource('github')}
                  isDisabled={pendingUploadSuiteId !== null}
                  icon={<GithubLogo size={18} />}
                  title="GitHub repository"
                  description="Run the suite from a linked repository."
                />
                <KindCard
                  isSelected={sourceKind === 'upload'}
                  onSelect={() => chooseSource('upload')}
                  isDisabled={pendingUploadSuiteId !== null}
                  icon={<UploadSimple size={18} />}
                  title="Uploaded bundle"
                  description="Upload a Playwright .tar.gz bundle."
                />
              </Flex>
              {sourceKind === 'github' ? (
                <>
                  <Select
                    label="Application"
                    description="Which application this suite tests. Its repositories, environments and personas are what you can pick below."
                    placeholder="Choose an application"
                    items={propertyOptions}
                    selectedKey={propertyId}
                    isDisabled={pendingUploadSuiteId !== null}
                    onSelectionChange={(key) => chooseProperty(String(key))}
                  />
                  {propertyId && (
                    <>
                      <RepoPicker
                        repos={external.repos.data?.items ?? []}
                        isGitHubConnected={external.githubStatus.data?.ok === true}
                        propertyId={propertyId}
                        selectedRepoId={repoId}
                        onSelectRepo={setRepoId}
                        repoRef={repoRef}
                        onRepoRefChange={setRepoRef}
                      />
                      <Select
                        label="Target environment"
                        description="Where the suite runs. You can change this later."
                        placeholder="Choose an environment"
                        items={environmentOptions}
                        selectedKey={environmentId}
                        isDisabled={pendingUploadSuiteId !== null}
                        onSelectionChange={(key) => setEnvironmentId(String(key))}
                        emptyText="This application has no environments yet"
                      />
                      <Select
                        label="Login persona"
                        description="The test account your suite logs in as. Use a least-privilege one."
                        placeholder="Choose a persona"
                        items={credentialOptions}
                        selectedKey={credentialId}
                        isDisabled={pendingUploadSuiteId !== null}
                        onSelectionChange={(key) => setCredentialId(String(key))}
                        emptyText="This application has no credentials yet"
                      />
                      <Text size="xs" className="text-muted-foreground">
                        You&rsquo;ll map the environment variables your tests read on the next
                        screen — the suite cannot run until at least one is mapped.
                      </Text>
                    </>
                  )}
                </>
              ) : (
                <Flex direction="column" gap="sm">
                  <Select
                    label="Application"
                    description="Which application this suite tests and where its environment and persona links are scoped."
                    placeholder="Choose an application"
                    items={propertyOptions}
                    selectedKey={propertyId}
                    isDisabled={pendingUploadSuiteId !== null}
                    onSelectionChange={(key) => chooseProperty(String(key))}
                  />
                  {propertyId ? (
                    <>
                      <Select
                        label="Target environment"
                        description={
                          sourceKind === 'image'
                            ? 'Choose a Sandbox v2 environment with an application image.'
                            : 'Where the suite runs. You can change this later.'
                        }
                        placeholder="Choose an environment"
                        items={environmentOptions}
                        selectedKey={environmentId}
                        isDisabled={pendingUploadSuiteId !== null}
                        onSelectionChange={(key) => setEnvironmentId(String(key))}
                        emptyText={
                          sourceKind === 'image'
                            ? 'This application has no Sandbox v2 environments yet'
                            : 'This application has no environments yet'
                        }
                      />
                      <Select
                        label="Login persona"
                        description="The test account your suite logs in as. Use a least-privilege one."
                        placeholder="Choose a persona"
                        items={credentialOptions}
                        selectedKey={credentialId}
                        isDisabled={pendingUploadSuiteId !== null}
                        onSelectionChange={(key) => setCredentialId(String(key))}
                        emptyText="This application has no credentials yet"
                      />
                      {sourceKind === 'upload' && (
                        <>
                          <input
                            ref={bundleInputRef}
                            type="file"
                            accept=".tar.gz,application/gzip,application/x-gzip"
                            className="sr-only"
                            disabled={uploadProgress !== null}
                            onChange={(event) => {
                              chooseBundle(event.target.files?.[0]);
                              event.currentTarget.value = '';
                            }}
                          />
                          <Flex align="center" gap="sm">
                            <Button
                              variant="secondary"
                              isDisabled={uploadProgress !== null}
                              onPress={() => bundleInputRef.current?.click()}
                            >
                              {pendingUploadSuiteId
                                ? 'Choose replacement bundle'
                                : 'Choose .tar.gz bundle'}
                            </Button>
                            <Text size="sm" className="text-muted-foreground">
                              {bundle
                                ? `${bundle.name} (${Math.ceil(bundle.size / 1024 / 1024)} MB)`
                                : 'No bundle selected'}
                            </Text>
                          </Flex>
                          {uploadProgress !== null ? (
                            <Progress
                              value={uploadProgress}
                              size="sm"
                              showLabel
                              aria-label="Bundle upload progress"
                            />
                          ) : null}
                          <Text size="xs" className="text-muted-foreground">
                            Playwright bundles must be 100 MB or smaller compressed. The server
                            validates archive paths before storing them.
                          </Text>
                          {pendingUploadSuiteId ? (
                            <Text size="xs" className="text-muted-foreground">
                              Retrying the upload for the suite you already created.
                            </Text>
                          ) : null}
                        </>
                      )}
                    </>
                  ) : null}
                </Flex>
              )}
            </>
          )}

          {error && (
            <Text size="sm" className="text-destructive">
              {error}
            </Text>
          )}
        </Flex>
        <DialogFooter>
          <Button
            variant="secondary"
            isDisabled={uploadProgress !== null || uploadTransactionPending}
            onPress={() => handleDialogOpenChange(false)}
          >
            Cancel
          </Button>
          <Button variant="primary" onPress={() => void submit()} isDisabled={!canSubmit}>
            {uploadProgress !== null
              ? 'Uploading…'
              : uploadTransactionPending
                ? 'Creating…'
                : pendingUploadSuiteId
                  ? 'Retry upload'
                  : 'Create'}
          </Button>
        </DialogFooter>
      </Dialog>
    </DialogTrigger>
  );
}
